
import { 
  fetchMissionAgents, 
  fetchCronJobs, 
  fetchAgentHistory as fetchHistoryApi,
  fetchAgentFile,
  readAgentFile,
  sendAgentMessage,
  pingAgent,
  fetchConversationReplay,
  fetchTranscript,
  sendAgentCommand,
} from '../lib/api.js';
import { renderMarkdown } from '../lib/markdown.js';

// Known agent configurations (visual overrides)
// New agents auto-appear with generated names/emojis
const AGENT_CONFIG = {
  main: { name: 'OmniClaw', emoji: '🐙', gradientClass: 'mc-avatar-main' },
  pulse: { name: 'Pulse Aria', emoji: '🎨', gradientClass: 'mc-avatar-pulse' },
  sentinel: { name: 'Sentinel', emoji: '🛡️', gradientClass: 'mc-avatar-sentinel' },
  'reviewer-vps': { name: 'Reviewer VPS', emoji: '🎯', gradientClass: 'mc-avatar-reviewer-vps' },
  'reviewer-local': { name: 'Reviewer Local', emoji: '🔬', gradientClass: 'mc-avatar-default' },
};

// Auto-generate a gradient class for unknown agents
const GRADIENT_POOL = [
  'mc-avatar-main', 'mc-avatar-pulse', 'mc-avatar-sentinel', 
  'mc-avatar-reviewer-vps', 'mc-avatar-default'
];

// Auto-generate emoji for unknown agents
const EMOJI_POOL = ['🤖', '⚡', '🔮', '🧠', '🚀', '💎', '🌟', '🔥', '🎭', '🦾'];

function getAgentConfig(agentId, rawAgent) {
  if (AGENT_CONFIG[agentId]) return AGENT_CONFIG[agentId];
  
  // Use config name from gateway if available
  const configName = rawAgent?._configName;
  
  // Auto-generate config for unknown agents
  const hash = agentId.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const prettyName = configName || agentId
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
  
  return {
    name: prettyName,
    emoji: EMOJI_POOL[hash % EMOJI_POOL.length],
    gradientClass: GRADIENT_POOL[hash % GRADIENT_POOL.length],
  };
}

// Abbreviate model names for compact display
function shortModel(model) {
  if (!model) return '';
  return model
    .replace('claude-opus-4-6-thinking', 'Opus 4.6')
    .replace('claude-opus-4-5-thinking', 'Opus 4.5')
    .replace('claude-sonnet-4-5-thinking', 'Sonnet 4.5')
    .replace('claude-sonnet-4-5', 'Sonnet 4.5')
    .replace('gemini-3-pro-high', 'Gemini 3 Pro')
    .replace('gemini-3-pro', 'Gemini 3 Pro')
    .replace('gemini-3-flash', 'Gemini 3 Flash')
    .replace('gemini-2.5-pro', 'Gemini 2.5 Pro')
    .replace(/^claude-/, 'Claude ')
    .replace(/^gemini-/, 'Gemini ');
}

// State management for Mission Control
let state = {
  agents: [],
  rawSessions: [], // Store ALL raw sessions for filtering
  cronJobs: [],
  history: {}, // Map of agentId -> messages
  stats: {
    activeAgents: 0,
    totalSessions: 0,
    tokensUsed: 0,
    estCost: 0,
    cronActive: 0,
    uptime: 0
  },
  prevStats: null, // Previous stats for animation
  statsHistory: [], // Last 7 data points for sparklines
  selectedAgentId: null,
  activeTab: 'conversations', // 'conversations', 'documents', 'cron', 'metrics'
  pollingInterval: null,
  isInitialized: false,
  isLoading: true,
  lastUpdated: null,
  searchQuery: '',
  // Feature 1: Quick Actions
  messageModal: null, // { agentId, sessionKey, environment, sending }
  pingResults: {}, // Map of agentId -> { status, latency, timestamp }
  // Feature 2: Document Viewer
  docViewer: null, // { docId, title, content, loading, metadata }
  // Feature 3: Activity Stream
  activityFilter: 'all', // 'all' | 'vps' | 'local'
  // Feature 4: Conversation Replay
  replaySession: null, // { sessionKey, environment, messages, loading }
  // Feature 6: Agent Commands
  commandModal: null, // { type, agent, loading }
};

// Initialize Mission Control
export async function initMissionControl() {
  if (state.isInitialized) return;
  state.isInitialized = true;
  
  // If we have cached data from a previous visit, render immediately
  const hasCachedData = state.agents.length > 0 || state.rawSessions.length > 0;
  if (hasCachedData) {
    state.isLoading = false;
    rerenderMissionControl();
    // Refresh data in background
    fetchAllData().then(() => {
      state._isPollingUpdate = true;
      rerenderMissionControl();
    });
  } else {
    state.isLoading = true;
    rerenderMissionControl();
    await fetchAllData();
    state.isLoading = false;
    rerenderMissionControl();
  }

  // Start polling — targeted updates only (no full DOM replacement)
  state.pollingInterval = setInterval(async () => {
    try {
      const prevSessionCount = state.rawSessions.length;
      const prevCronCount = state.cronJobs.length;
      await fetchAllData();
      
      // Track if data actually changed
      const dataChanged = state.rawSessions.length !== prevSessionCount || 
                          state.cronJobs.length !== prevCronCount;
      
      state._isPollingUpdate = true;
      state._dataChanged = dataChanged;
      state.lastUpdated = Date.now();
      rerenderMissionControl();
      
      // Update connection indicator
      const indicator = document.querySelector('.mc-connection-status');
      if (indicator) {
        indicator.classList.remove('disconnected');
        indicator.classList.add('connected');
        indicator.title = `Last update: ${new Date().toLocaleTimeString()}`;
      }
    } catch (err) {
      console.error('[MC] Polling error:', err);
      const indicator = document.querySelector('.mc-connection-status');
      if (indicator) {
        indicator.classList.remove('connected');
        indicator.classList.add('disconnected');
      }
    }
  }, 10000); // 10s poll
}

// Cleanup
export function cleanupMissionControl() {
  if (state.pollingInterval) {
    clearInterval(state.pollingInterval);
    state.pollingInterval = null;
  }
  state.isInitialized = false;
}

// Fetch all data from APIs
async function fetchAllData() {
  try {
    const [agentsData, cronData] = await Promise.all([
      fetchMissionAgents(),
      fetchCronJobs()
    ]);

    // Store ALL raw sessions for filtering
    const rawAgents = agentsData.agents || [];
    state.rawSessions = rawAgents; // Keep all 171+ sessions
    console.log('[MC] Raw sessions from API:', rawAgents.length);

    // Process Agents (deduplicated by agent+env)
    const processedAgents = processAgents(rawAgents);
    console.log('[MC] Processed agents:', processedAgents.length, processedAgents.map(a => a.id));
    state.agents = processedAgents;

    // Process Cron
    state.cronJobs = cronData.jobs || [];
    console.log('[MC] Cron jobs:', state.cronJobs.length);

    // Update Stats (with animation tracking)
    updateStats();

    // Fetch history for selected agent if exists
    if (state.selectedAgentId && !state.history[state.selectedAgentId]) {
       await fetchAgentHistory(state.selectedAgentId);
    }

    state.lastUpdated = new Date();
  } catch (error) {
    console.error('Failed to fetch Mission Control data:', error);
  }
}

async function fetchAgentHistory(agentId) {
  // Find the session key for this agent
  const agent = state.agents.find(a => a.id === agentId);
  if (!agent) return;

  try {
    const data = await fetchHistoryApi(agent.sessionKey);
    state.history[agentId] = data.messages || [];
  } catch (err) {
    console.error(`Failed to fetch history for ${agentId}:`, err);
    state.history[agentId] = [];
  }
}

// Process and normalize agent data
function processAgents(rawAgents) {
  // Group by agentId + environment (so VPS and Local agents are separate)
  const agentMap = new Map();
  
  for (const raw of rawAgents) {
    const key = raw.key || '';
    if (!key.startsWith('agent:')) continue;
    
    const parts = key.split(':');
    const agentId = parts[1]; 
    if (!agentId) continue;

    const env = (raw.environment || 'VPS').toLowerCase();
    const compositeKey = `${agentId}:${env}`; // e.g., "main:vps", "main:local"

    const existing = agentMap.get(compositeKey);
    
    // Pick the most recent session for each agent+environment combo
    if (!existing || (raw.updatedAt && (!existing.updatedAt || new Date(raw.updatedAt) > new Date(existing.updatedAt)))) {
      agentMap.set(compositeKey, { ...raw, simpleId: agentId, compositeId: compositeKey });
    }
  }

  // Convert to array and enhance with config
  return Array.from(agentMap.values()).map(agent => {
    const config = getAgentConfig(agent.simpleId, agent);
    const env = (agent.environment || 'vps').toLowerCase();
    
    return {
      ...agent,
      id: agent.compositeId, // "main:vps" — unique per agent+env
      agentId: agent.simpleId, // "main" — base agent ID
      sessionKey: agent.key,
      name: config.name,
      emoji: config.emoji,
      gradientClass: config.gradientClass,
      model: agent.model || 'Unknown',
      updatedAt: agent.updatedAt ? new Date(agent.updatedAt) : null,
      isActive: agent.updatedAt && (new Date() - new Date(agent.updatedAt) < 5 * 60 * 1000),
      isRecent: agent.updatedAt && (new Date() - new Date(agent.updatedAt) < 30 * 60 * 1000),
      environment: env,
    };
  }).sort((a, b) => {
      // Sort priority: Main -> Pulse -> Sentinel -> Others
      const priority = ['main', 'pulse', 'sentinel', 'reviewer-vps', 'reviewer-local'];
      const pA = priority.indexOf(a.agentId);
      const pB = priority.indexOf(b.agentId);
      if (pA !== -1 && pB !== -1) return pA - pB;
      if (pA !== -1) return -1;
      if (pB !== -1) return 1;
      return a.name.localeCompare(b.name);
  });
}

function updateStats() {
  // Save previous stats for animation
  state.prevStats = { ...state.stats };

  const active = state.agents.filter(a => a.isActive).length;

  // Calculate real token usage from raw sessions
  const totalTokens = state.rawSessions.reduce((sum, s) => sum + (s.totalTokens || 0), 0);
  const estCost = (totalTokens / 1000000) * 15; // ~$15 per 1M tokens (rough estimate)

  state.stats = {
    activeAgents: active,
    totalSessions: state.rawSessions.length,
    cronActive: state.cronJobs.filter(j => j.enabled).length,
    cronTotal: state.cronJobs.length,
    tokensUsed: totalTokens,
    estCost: estCost,
    uptime: '4h 32m' // TODO: Get real data
  };

  // Update sparkline history (keep last 7 data points)
  state.statsHistory.push({
    activeAgents: active,
    totalSessions: state.rawSessions.length,
    tokensUsed: totalTokens,
    timestamp: Date.now()
  });
  if (state.statsHistory.length > 7) {
    state.statsHistory.shift();
  }
}


// ─────────────────────────────────────────────────────────────────────────────
// ANIMATION & CHART HELPERS
// ─────────────────────────────────────────────────────────────────────────────

// Animate number changes in stats cards
function animateStatChanges() {
  if (!state.prevStats) return;

  const animateNumber = (selector, from, to, duration = 500) => {
    const element = document.querySelector(selector);
    if (!element || from === to) return;

    const start = performance.now();
    const update = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      const current = Math.round(from + (to - from) * eased);

      if (selector.includes('estCost')) {
        element.textContent = `$${current.toFixed(2)}`;
      } else {
        element.textContent = current.toLocaleString();
      }

      if (progress < 1) requestAnimationFrame(update);
    };
    requestAnimationFrame(update);
  };

  // Animate each stat
  animateNumber('[data-stat="activeAgents"]', state.prevStats.activeAgents, state.stats.activeAgents);
  animateNumber('[data-stat="totalSessions"]', state.prevStats.totalSessions, state.stats.totalSessions);
  animateNumber('[data-stat="tokensUsed"]', state.prevStats.tokensUsed, state.stats.tokensUsed);
  animateNumber('[data-stat="estCost"]', state.prevStats.estCost, state.stats.estCost);
}

// Generate sparkline SVG path from history
function generateSparkline(dataKey) {
  if (state.statsHistory.length < 2) {
    // Not enough data, generate fake data for visual
    return '0,15 10,8 20,12 30,5 40,18 50,10 60,14';
  }

  const values = state.statsHistory.map(h => h[dataKey] || 0);
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;

  return values.map((val, i) => {
    const x = (i / (values.length - 1)) * 60;
    const y = 20 - ((val - min) / range) * 18; // Invert Y, leave 2px padding
    return `${x},${y}`;
  }).join(' ');
}

// Render token usage bar chart on canvas
function renderTokenChart() {
  const canvas = document.getElementById('mc-token-chart');
  if (!canvas) return;

  const ctx = canvas.getContext('2d');
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();

  // Set actual size in memory (scaled to account for extra pixel density)
  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;

  // Scale context to match DPI
  ctx.scale(dpr, dpr);

  // Use CSS size
  canvas.style.width = rect.width + 'px';
  canvas.style.height = rect.height + 'px';

  // Clear canvas
  ctx.clearRect(0, 0, rect.width, rect.height);

  // Get agents with token data
  const agentsWithTokens = state.agents.map(agent => {
    const agentSessions = state.rawSessions.filter(s =>
      s.key.startsWith(`agent:${agent.agentId}:`) &&
      s.environment?.toLowerCase() === agent.environment
    );
    const totalTokens = agentSessions.reduce((sum, s) => sum + (s.totalTokens || 0), 0);
    return { ...agent, totalTokens };
  }).filter(a => a.totalTokens > 0).sort((a, b) => b.totalTokens - a.totalTokens);

  if (agentsWithTokens.length === 0) {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.font = '14px var(--font-sans)';
    ctx.textAlign = 'center';
    ctx.fillText('No token usage data available', rect.width / 2, rect.height / 2);
    return;
  }

  const maxTokens = Math.max(...agentsWithTokens.map(a => a.totalTokens));
  const barWidth = Math.min(60, (rect.width - 40) / agentsWithTokens.length - 10);
  const chartHeight = rect.height - 60;
  const startX = (rect.width - (agentsWithTokens.length * (barWidth + 10) - 10)) / 2;

  // Define gradient colors for each agent (matching CSS gradients)
  const gradientColors = {
    'mc-avatar-main': ['hsl(270, 70%, 50%)', 'hsl(290, 70%, 40%)'],
    'mc-avatar-pulse': ['hsl(210, 80%, 50%)', 'hsl(230, 80%, 40%)'],
    'mc-avatar-sentinel': ['hsl(150, 70%, 40%)', 'hsl(170, 70%, 30%)'],
    'mc-avatar-reviewer-vps': ['hsl(25, 80%, 50%)', 'hsl(15, 80%, 40%)'],
    'mc-avatar-default': ['hsl(200, 60%, 45%)', 'hsl(220, 60%, 35%)']
  };

  agentsWithTokens.forEach((agent, i) => {
    const x = startX + i * (barWidth + 10);
    const barHeight = (agent.totalTokens / maxTokens) * chartHeight;
    const y = chartHeight - barHeight + 10;

    // Create gradient
    const colors = gradientColors[agent.gradientClass] || ['hsl(200, 60%, 45%)', 'hsl(220, 60%, 35%)'];
    const gradient = ctx.createLinearGradient(x, y, x, y + barHeight);
    gradient.addColorStop(0, colors[0]);
    gradient.addColorStop(1, colors[1]);

    // Draw bar
    ctx.fillStyle = gradient;
    ctx.fillRect(x, y, barWidth, barHeight);

    // Draw agent emoji
    ctx.font = '20px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(agent.emoji, x + barWidth / 2, y - 8);

    // Draw token count
    ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.font = '11px var(--font-sans)';
    const tokenLabel = agent.totalTokens > 1000 ? `${(agent.totalTokens / 1000).toFixed(1)}k` : agent.totalTokens;
    ctx.fillText(tokenLabel, x + barWidth / 2, rect.height - 35);

    // Draw agent name
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.font = '10px var(--font-sans)';
    const name = agent.name.length > 10 ? agent.name.substring(0, 8) + '...' : agent.name;
    ctx.fillText(name, x + barWidth / 2, rect.height - 20);
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// RENDER FUNCTIONS
// ─────────────────────────────────────────────────────────────────────────────

export function renderMissionControl() {
  return `
    <div class="mission-control-container animate-fade-in">
      ${renderStatsRibbon()}
      <div class="mc-layout">
        ${renderAgentRoster()}
        ${renderMainContent()}
        ${renderInspectorPanel()}
      </div>
      ${renderCommandBar()}
      ${state.messageModal ? renderMessageModal() : ''}
      ${state.docViewer ? renderDocViewer() : ''}
      ${state.replaySession ? renderConversationReplay() : ''}
      ${state.commandModal ? renderCommandModal() : ''}
    </div>
  `;
}

// Targeted re-render — avoids full DOM replacement to preserve scroll & interaction state
export function rerenderMissionControl() {
  const container = document.querySelector('.mission-control-container');
  console.log('[MC] rerenderMissionControl called, container found:', !!container, 'agents:', state.agents.length);
  if (!container) return;

  // If this is a polling update (not user-triggered), do targeted updates only
  if (state._isPollingUpdate) {
    state._isPollingUpdate = false;
    // Only update stats ribbon values (no DOM replacement)
    const s = state.stats;
    const updateText = (sel, val) => {
      const el = container.querySelector(sel);
      if (el && el.textContent !== String(val)) el.textContent = val;
    };
    updateText('[data-stat="activeAgents"]', s.activeAgents);
    updateText('[data-stat="totalSessions"]', s.totalSessions);
    updateText('[data-stat="tokensUsed"]', s.tokensUsed?.toLocaleString() || '0');
    updateText('[data-stat="estCost"]', s.estCost?.toFixed(2) || '0');
    // Update sparklines
    container.querySelectorAll('.mc-sparkline-svg polyline').forEach((el, i) => {
      const key = i === 0 ? 'totalSessions' : 'tokensUsed';
      el.setAttribute('points', generateSparkline(key));
    });
    animateStatChanges();
    return;
  }

  // Full re-render for user-triggered changes (agent select, deselect)
  // Save scroll position
  const scrollY = container.closest('.mission-control-container')?.scrollTop || 0;
  container.outerHTML = renderMissionControl();
  attachEventListeners();
  animateStatChanges();
  renderTokenChart();
  // Restore scroll position
  requestAnimationFrame(() => {
    const newContainer = document.querySelector('.mission-control-container');
    if (newContainer) newContainer.scrollTop = scrollY;
  });
}

function renderStatsRibbon() {
  const s = state.stats;
  return `
    <div class="mc-stats-ribbon">
      <div class="mc-stat-card hover-lift">
        <div class="mc-stat-label">Active Agents</div>
        <div class="mc-stat-value">
          <span class="mc-status-dot ${s.activeAgents > 0 ? 'pulse' : ''}"></span>
          <span data-stat="activeAgents">${s.activeAgents}</span>
        </div>
      </div>

      <div class="mc-stat-card hover-lift">
        <div class="mc-stat-label">Total Sessions</div>
        <div class="mc-stat-value">
          <span data-stat="totalSessions">${s.totalSessions}</span>
          <svg class="mc-sparkline-svg" viewBox="0 0 60 20" preserveAspectRatio="none">
            <polyline points="${generateSparkline('totalSessions')}" fill="none" stroke="var(--accent-primary)" stroke-width="1.5"/>
          </svg>
        </div>
      </div>

      <div class="mc-stat-card hover-lift">
        <div class="mc-stat-label">Cron Jobs</div>
        <div class="mc-stat-value">
           ${s.cronActive} <span class="text-muted text-sm">/ ${s.cronTotal}</span>
        </div>
      </div>

      <div class="mc-stat-card hover-lift">
        <div class="mc-stat-label">Tokens (24h)</div>
        <div class="mc-stat-value">
          <span data-stat="tokensUsed">${s.tokensUsed.toLocaleString()}</span>
          <svg class="mc-sparkline-svg" viewBox="0 0 60 20" preserveAspectRatio="none">
            <polyline points="${generateSparkline('tokensUsed')}" fill="none" stroke="var(--accent-success)" stroke-width="1.5"/>
          </svg>
        </div>
      </div>

      <div class="mc-stat-card hover-lift">
        <div class="mc-stat-label">Est. Cost (Today)</div>
        <div class="mc-stat-value">
          <span data-stat="estCost">$${s.estCost.toFixed(2)}</span>
          <span class="mc-stat-trend up">↑</span>
        </div>
      </div>

      <div class="mc-stat-card hover-lift">
        <div class="mc-stat-label">Uptime</div>
        <div class="mc-stat-value text-xl">${s.uptime}</div>
      </div>

      <div class="mc-stat-card hover-lift mc-connection-card">
        <div class="mc-stat-label">Status</div>
        <div class="mc-stat-value">
          <span class="mc-connection-status connected" title="Connected"></span>
          <span class="text-sm">Live</span>
        </div>
      </div>
    </div>
  `;
}

function renderAgentRoster() {
  // Filter agents based on search query
  const filteredAgents = state.searchQuery
    ? state.agents.filter(a =>
        a.name.toLowerCase().includes(state.searchQuery.toLowerCase()) ||
        a.agentId.toLowerCase().includes(state.searchQuery.toLowerCase())
      )
    : state.agents;

  const vpsAgents = filteredAgents.filter(a => a.environment === 'vps' || !a.environment);
  const localAgents = filteredAgents.filter(a => a.environment === 'local');
  console.log('[MC] renderAgentRoster: total agents:', state.agents.length, 'filtered:', filteredAgents.length, 'vps:', vpsAgents.length, 'local:', localAgents.length);

  return `
    <div class="mc-roster">
      <div class="mc-roster-header">
        <div class="mc-roster-title">Agent Roster</div>
      </div>
      <div class="mc-roster-body custom-scrollbar">
        ${vpsAgents.length > 0 ? renderAgentGroup('VPS Agents', vpsAgents) : ''}
        ${localAgents.length > 0 ? renderAgentGroup('Local Agents', localAgents) : ''}
        ${filteredAgents.length === 0 ? '<div class="mc-empty-state">No agents found</div>' : ''}
      </div>
    </div>
  `;
}

function renderAgentGroup(title, agents) {
  return `
    <div class="mc-agent-group">
      <div class="mc-agent-group-label">${title}</div>
      ${agents.map(agent => renderAgentCard(agent)).join('')}
    </div>
  `;
}

function renderAgentCard(agent) {
  const isSelected = state.selectedAgentId === agent.id;
  const statusClass = agent.isActive ? 'status-active' : (agent.isRecent ? 'status-recent' : 'status-idle');
  const lastActiveText = agent.updatedAt ? timeAgo(agent.updatedAt) : 'Never';

  return `
    <div class="mc-agent-card ${isSelected ? 'selected' : ''}" data-agent-id="${agent.id}" onclick="window.mcSelectAgent('${agent.id}')" role="button" tabindex="0">
      <div class="mc-agent-avatar-container">
        <div class="mc-agent-avatar ${agent.gradientClass}">
          ${agent.emoji}
        </div>
        <div class="mc-agent-status-ring ${statusClass}"></div>
      </div>
      <div class="mc-agent-info">
        <div class="mc-agent-name">${escapeHtml(agent.name)}</div>
        <div class="mc-agent-meta">
          <span class="mc-agent-model">${shortModel(agent.model)}</span>
        </div>
      </div>
      <div class="mc-agent-activity">
        ${lastActiveText}
      </div>
    </div>
  `;
}

function renderMainContent() {
  const agent = state.selectedAgentId ? state.agents.find(a => a.id === state.selectedAgentId) : null;
  return `
    <div class="mc-main-content custom-scrollbar">
      ${state.selectedAgentId ? renderAgentDetail() : renderDashboardOverview()}
    </div>
    ${agent ? `<button class="mc-mobile-fab" onclick="window.mcShowMobileActions()" title="Actions">⚡</button>` : ''}
  `;
}

function renderDashboardOverview() {
  return `
    <div class="mc-dashboard-overview animate-fade-in-up">
       <div class="mc-activity-header">
         <h2 class="mc-section-title">Activity Stream</h2>
         <div class="mc-activity-filters">
           <button class="mc-filter-btn ${state.activityFilter === 'all' ? 'active' : ''}" onclick="window.mcSetActivityFilter('all')">All</button>
           <button class="mc-filter-btn ${state.activityFilter === 'vps' ? 'active' : ''}" onclick="window.mcSetActivityFilter('vps')">VPS</button>
           <button class="mc-filter-btn ${state.activityFilter === 'local' ? 'active' : ''}" onclick="window.mcSetActivityFilter('local')">Local</button>
         </div>
       </div>
       <div class="mc-activity-stream">
          ${renderActivityStream()}
       </div>

       <h2 class="mc-section-title mt-8">Cron Timeline</h2>
       <div class="mc-cron-timeline-container">
          ${renderCronTimeline()}
       </div>

       <h2 class="mc-section-title mt-8">Token Usage (24h)</h2>
       <div class="mc-chart-container">
          <canvas id="mc-token-chart" width="800" height="200"></canvas>
       </div>
    </div>
  `;
}

function renderActivityStream() {
  // Build a real activity feed from sessions, cron jobs, and agent activity
  const now = Date.now();
  const activities = [];

  // Get recent sessions (last 24h)
  const recentSessions = (state.rawSessions || [])
    .filter(s => s.updatedAt && (now - new Date(s.updatedAt).getTime()) < 86400000)
    .filter(s => {
      if (state.activityFilter === 'all') return true;
      return (s.environment || 'vps').toLowerCase() === state.activityFilter;
    });

  // Group sessions by agent to avoid flooding
  const agentSessionMap = {};
  recentSessions.forEach(s => {
    const key = s.key || '';
    const agentId = key.split(':')[1] || 'unknown';
    const env = (s.environment || 'vps').toLowerCase();
    const compositeId = `${agentId}:${env}`;
    if (!agentSessionMap[compositeId]) agentSessionMap[compositeId] = [];
    agentSessionMap[compositeId].push(s);
  });

  // Create activity entries from sessions
  Object.entries(agentSessionMap).forEach(([compositeId, sessions]) => {
    const agent = state.agents.find(a => a.id === compositeId);
    if (!agent) return;

    // Most recent session
    const latest = sessions.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))[0];
    const label = latest.label || latest.key || '';
    const tokens = sessions.reduce((sum, s) => sum + (s.totalTokens || 0), 0);

    // Determine activity type from session key/label
    let icon = '💬', action = 'Active session';
    if (label.includes('cron:') || latest.key?.includes(':cron:')) {
      icon = '⏰'; action = 'Cron job completed';
    } else if (latest.key?.includes(':spawn:')) {
      icon = '🔀'; action = 'Spawned task';
    } else if (label.toLowerCase().includes('digest')) {
      icon = '📰'; action = 'Generated digest';
    } else if (label.toLowerCase().includes('review')) {
      icon = '🔍'; action = 'Code review';
    } else if (label.toLowerCase().includes('check') || label.toLowerCase().includes('health')) {
      icon = '🏥'; action = 'Health check';
    } else if (label.toLowerCase().includes('news') || label.toLowerCase().includes('research')) {
      icon = '📡'; action = 'Research/news scan';
    } else if (sessions.length > 5) {
      icon = '🔧'; action = `Active across ${sessions.length} sessions`;
    }

    activities.push({
      agent,
      icon,
      action,
      detail: label.replace(/^cron:\s*/i, '').trim() || null,
      time: new Date(latest.updatedAt),
      tokens,
      sessionCount: sessions.length,
      sessionKey: latest.key,
    });
  });

  // Add cron job activities
  state.cronJobs
    .filter(j => j.lastRunAt && (now - new Date(j.lastRunAt).getTime()) < 86400000)
    .filter(j => {
      if (state.activityFilter === 'all') return true;
      return (j.environment || 'vps').toLowerCase() === state.activityFilter;
    })
    .forEach(j => {
      // Avoid duplicating if already covered by session
      const name = j.name || j.id || '';
      if (activities.some(a => a.detail?.toLowerCase() === name.toLowerCase())) return;
      
      activities.push({
        agent: null,
        icon: j.lastRunStatus === 'success' ? '✅' : '❌',
        action: `Cron: ${name}`,
        detail: formatSchedule(j.schedule),
        time: new Date(j.lastRunAt),
        tokens: 0,
        sessionCount: 0,
        sessionKey: null,
        cronJob: true,
        env: (j.environment || 'VPS'),
      });
    });

  // Sort by time, most recent first
  activities.sort((a, b) => b.time - a.time);

  if (activities.length === 0) {
    return `<div class="mc-empty-state">No recent activity</div>`;
  }

  return activities.slice(0, 20).map((item, index) => {
    const agentAvatar = item.agent 
      ? `<div class="mc-activity-avatar ${item.agent.gradientClass}">${item.agent.emoji}</div>`
      : `<div class="mc-activity-avatar mc-avatar-default">⏰</div>`;
    const agentName = item.agent?.name || item.env || 'System';
    
    return `
    <div class="mc-activity-item mc-stagger-item" style="animation-delay: ${index * 40}ms"
         ${item.agent ? `onclick="window.mcSelectAgent('${item.agent.id}')" role="button"` : ''}>
      <div class="mc-activity-icon">${item.icon}</div>
      ${agentAvatar}
      <div class="mc-activity-content">
        <div class="mc-activity-primary">
          <span class="font-bold">${escapeHtml(agentName)}</span>
          <span class="text-secondary">${item.action}</span>
        </div>
        ${item.detail ? `<div class="mc-activity-detail">${escapeHtml(item.detail)}</div>` : ''}
        <div class="mc-activity-meta">
          ${item.tokens > 0 ? `<span class="mc-token-badge">${(item.tokens / 1000).toFixed(1)}k tokens</span>` : ''}
          ${item.sessionCount > 1 ? `<span class="mc-session-badge">${item.sessionCount} sessions</span>` : ''}
          <span class="mc-activity-time">${timeAgo(item.time)}</span>
        </div>
      </div>
    </div>
  `}).join('');
}

function formatSchedule(schedule) {
  if (!schedule) return 'No schedule';
  if (typeof schedule === 'string') return schedule;
  if (schedule.kind === 'cron') return `cron: ${schedule.expr || ''}${schedule.tz ? ` (${schedule.tz})` : ''}`;
  if (schedule.kind === 'every') {
    const ms = schedule.everyMs || 0;
    if (ms >= 3600000) return `Every ${(ms / 3600000).toFixed(1)}h`;
    if (ms >= 60000) return `Every ${Math.round(ms / 60000)}m`;
    return `Every ${Math.round(ms / 1000)}s`;
  }
  if (schedule.kind === 'at') return `Once at ${new Date(schedule.at).toLocaleString()}`;
  return JSON.stringify(schedule);
}

function renderCronTimeline() {
  if (state.cronJobs.length === 0) return '<div class="mc-empty-state">No cron jobs configured</div>';

  return `
    <div class="mc-cron-timeline">
       ${state.cronJobs.slice(0, 8).map((job, idx) => {
         const statusClass = job.enabled ? 'enabled' : 'disabled';
         const lastRunStatus = job.lastRunStatus || 'success';
         const nextRun = job.nextRunAt ? timeAgo(new Date(job.nextRunAt)) : 'Not scheduled';

         return `
         <div class="mc-cron-job-row mc-stagger-item" style="animation-delay: ${idx * 50}ms">
           <div class="mc-cron-status-dot ${statusClass}"></div>
           <div class="mc-cron-job-info">
             <div class="mc-cron-job-name">${escapeHtml(job.name || job.id)}</div>
             <div class="mc-cron-job-schedule">${formatSchedule(job.schedule)}</div>
           </div>
           <div class="mc-cron-job-bar">
             <div class="mc-cron-bar-track"></div>
             <div class="mc-cron-bar-fill ${lastRunStatus}"></div>
           </div>
           <div class="mc-cron-next-run">
             <span class="mc-cron-label">Next:</span>
             <span class="mc-cron-time">${nextRun}</span>
           </div>
         </div>
         `;
       }).join('')}
    </div>
  `;
}


function renderAgentDetail() {
  const agent = state.agents.find(a => a.id === state.selectedAgentId);
  if (!agent) return renderDashboardOverview();

  return `
    <div class="mc-agent-detail animate-fade-in">
       <div class="mc-detail-header">
         <div class="mc-detail-avatar ${agent.gradientClass}">${agent.emoji}</div>
         <div class="mc-detail-title-block">
           <h1 class="mc-detail-name">${escapeHtml(agent.name)}</h1>
           <div class="mc-detail-meta">
             <span class="mc-badge">${agent.environment}</span>
             <span class="mc-badge secondary">${shortModel(agent.model)}</span>
             <span class="text-muted text-sm ml-2">Last active: ${agent.updatedAt ? timeAgo(agent.updatedAt) : 'Never'}</span>
           </div>
         </div>
         <button class="mc-btn mc-back-btn" onclick="window.mcDeselectAgent()" title="Back to Overview">← Back</button>
       </div>

       <div class="mc-tabs">
         <button class="mc-tab ${state.activeTab === 'conversations' ? 'active' : ''}" onclick="window.mcSwitchTab('conversations')">Conversations</button>
         <button class="mc-tab ${state.activeTab === 'documents' ? 'active' : ''}" onclick="window.mcSwitchTab('documents')">Documents</button>
         <button class="mc-tab ${state.activeTab === 'cron' ? 'active' : ''}" onclick="window.mcSwitchTab('cron')">Cron Jobs</button>
         <button class="mc-tab ${state.activeTab === 'metrics' ? 'active' : ''}" onclick="window.mcSwitchTab('metrics')">Metrics</button>
       </div>

       <div class="mc-tab-content">
         ${state.activeTab === 'conversations' ? renderConversationsTab(agent) : ''}
         ${state.activeTab === 'documents' ? renderDocumentsTab(agent) : ''}
         ${state.activeTab === 'cron' ? renderCronTab(agent) : ''}
         ${state.activeTab === 'metrics' ? renderMetricsTab(agent) : ''}
       </div>
    </div>
  `;
}

function renderConversationsTab(agent) {
  // Get ALL sessions for this agent from raw sessions
  const agentSessions = state.rawSessions
    .filter(s => s.key.startsWith(`agent:${agent.agentId}:`))
    .filter(s => s.environment?.toLowerCase() === agent.environment)
    .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));

  if (agentSessions.length === 0) {
    return `<div class="mc-empty-state">
      <div class="mc-empty-icon">💬</div>
      <div>No conversation sessions found</div>
      <div class="text-muted text-xs mt-2">Sessions will appear here when the agent is active</div>
    </div>`;
  }

  return `
    <div class="mc-session-list">
      ${agentSessions.map((session, idx) => {
        const sessionType = session.key.includes(':cron:') ? 'cron' :
                           session.key.includes(':spawned:') ? 'spawned' : 'main';
        const icon = sessionType === 'cron' ? '⏰' :
                    sessionType === 'spawned' ? '🔀' : '💬';
        const label = session.label || sessionType.toUpperCase();
        const timeText = session.updatedAt ? timeAgo(new Date(session.updatedAt)) : 'Unknown';
        const fullTime = session.updatedAt ? new Date(session.updatedAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
        const tokens = session.totalTokens || 0;
        const model = shortModel(session.model || 'Unknown');

        return `
        <div class="mc-session-card mc-stagger-item" style="animation-delay: ${idx * 40}ms"
             onclick="window.mcOpenReplay('${session.key}', '${agent.environment}', '${escapeHtml(session.transcriptPath || '')}', '${escapeHtml(agent.agentId || '')}')"
             role="button" title="Click to view conversation">
          <div class="mc-session-type">${icon}</div>
          <div class="mc-session-info">
            <span class="mc-session-label">${label}</span>
            <span class="mc-session-time">${timeText}${fullTime ? ` · ${fullTime}` : ''}</span>
          </div>
          <div class="mc-session-stats">
            <span class="mc-session-tokens">${tokens > 0 ? `${(tokens / 1000).toFixed(1)}k tokens` : 'No tokens'}</span>
            <span class="mc-session-model">${model}</span>
          </div>
          <div class="mc-session-replay-hint">▶</div>
        </div>
        `;
      }).join('')}
    </div>
  `;
}

// Favorites stored in localStorage
function getFavorites() {
  try {
    return JSON.parse(localStorage.getItem('mc-favorites') || '[]');
  } catch { return []; }
}

function toggleFavorite(docId) {
  const favs = getFavorites();
  const idx = favs.indexOf(docId);
  if (idx >= 0) favs.splice(idx, 1);
  else favs.push(docId);
  localStorage.setItem('mc-favorites', JSON.stringify(favs));
}

function renderDocumentsTab(agent) {
  // Extract documents from session labels, cron outputs, and known workspace files
  const agentSessions = (state.rawSessions || []).filter(s => {
    const key = s.key || '';
    return key.includes(`agent:${agent.agentId}:`);
  }).filter(s => {
    const env = (s.environment || s.env || 'vps').toLowerCase();
    return env === agent.environment;
  });

  const documents = [];
  const favorites = getFavorites();
  const seenNames = new Set();

  // Known workspace documents (pinned files that agents create/maintain)
  const knownDocs = {
    'main': [
      { name: 'Mission Control Plan', icon: '📋', type: 'Plan', path: '~/projects/chorus/MISSION_CONTROL_PLAN.md' },
      { name: 'Coding Protocol v1.2', icon: '📜', type: 'Protocol', path: '~/.openclaw/workspace/CODING_PROTOCOL.md' },
      { name: 'Memory Log (Today)', icon: '🧠', type: 'Memory', path: '~/.openclaw/workspace/memory/' },
      { name: 'Partnership Plan', icon: '🤝', type: 'Plan', path: '~/.openclaw/workspace/PARTNERSHIP.md' },
      { name: 'Agent Pool Config', icon: '🏊', type: 'Config', path: '~/.openclaw/agent-pool.json' },
    ],
    'pulse': [
      { name: 'Design System', icon: '🎨', type: 'Design', path: '~/.openclaw/workspace-pulse/DESIGN_SYSTEM.md' },
    ],
    'sentinel': [
      { name: 'Security Reports', icon: '🛡️', type: 'Report', path: '~/.openclaw/workspace/security/' },
    ]
  };

  // Add known workspace docs
  const agentKnownDocs = knownDocs[agent.agentId] || [];
  agentKnownDocs.forEach(doc => {
    const docId = `${agent.id}:file:${doc.path}`;
    documents.push({
      id: docId,
      name: doc.name,
      type: doc.type,
      icon: doc.icon,
      sessionKey: null,
      path: doc.path,
      tokens: 0,
      updated: null,
      isFav: favorites.includes(docId),
      source: 'Workspace',
      pinned: true
    });
    seenNames.add(doc.name.toLowerCase());
  });

  // Extract documents from session activity
  agentSessions.forEach(s => {
    const label = s.label || s.key || '';
    const sessionKey = s.key || '';
    const tokens = s.totalTokens || 0;
    const updated = s.updatedAt ? new Date(s.updatedAt) : null;
    
    // Cron outputs, spawned tasks, and labeled sessions are documents
    const isDoc = label.toLowerCase().includes('cron:') || 
        label.toLowerCase().includes('digest') || 
        label.toLowerCase().includes('report') || label.toLowerCase().includes('check') ||
        label.toLowerCase().includes('news') || label.toLowerCase().includes('research') ||
        label.toLowerCase().includes('backup') || label.toLowerCase().includes('review') ||
        label.toLowerCase().includes('plan') || label.toLowerCase().includes('analysis') ||
        label.toLowerCase().includes('summary') || label.toLowerCase().includes('practice') ||
        sessionKey.includes(':spawn:') || sessionKey.includes(':run:');
    
    if (isDoc) {
      const docName = label.replace(/^cron:\s*/i, '').trim() || 'Agent Task';
      if (seenNames.has(docName.toLowerCase())) return;
      seenNames.add(docName.toLowerCase());
      
      const docId = `${agent.id}:${sessionKey}`;
      documents.push({
        id: docId,
        name: docName,
        type: getDocType(label),
        icon: getDocIcon(label),
        sessionKey,
        path: null,
        tokens,
        updated,
        isFav: favorites.includes(docId),
        source: sessionKey.includes(':cron:') ? 'Cron Job' : sessionKey.includes(':spawn:') ? 'Spawned Task' : 'Session'
      });
    }
  });

  // Sort: favorites first, then pinned, then by date
  documents.sort((a, b) => {
    if (a.isFav !== b.isFav) return a.isFav ? -1 : 1;
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
    return (b.updated || 0) - (a.updated || 0);
  });

  if (documents.length === 0) {
    return `<div class="mc-empty-state">
      <div class="mc-empty-icon">📄</div>
      <div>No documents found for ${escapeHtml(agent.name)}</div>
      <div class="text-muted text-sm" style="margin-top: 8px">Documents are generated from cron jobs, reports, and agent sessions</div>
    </div>`;
  }

  return `
    <div class="mc-documents-list">
      <div class="mc-doc-header">
        <span class="text-muted text-sm">${documents.length} documents</span>
        <span class="text-muted text-sm">⭐ ${documents.filter(d => d.isFav).length} favorites</span>
      </div>
      ${documents.map((doc, idx) => `
        <div class="mc-doc-card mc-stagger-item ${doc.isFav ? 'mc-doc-fav' : ''} ${doc.pinned ? 'mc-doc-pinned' : ''}" 
             style="animation-delay: ${idx * 40}ms"
             onclick="window.mcOpenDoc('${doc.id}', '${(doc.path || '').replace(/'/g, "\\'")}', '${(doc.sessionKey || '').replace(/'/g, "\\'")}')"
             role="button">
          <div class="mc-doc-icon">${doc.icon}</div>
          <div class="mc-doc-info">
            <div class="mc-doc-name">${doc.pinned ? '📌 ' : ''}${escapeHtml(doc.name)}</div>
            <div class="mc-doc-meta">
              <span class="mc-badge small">${doc.type}</span>
              <span class="mc-badge small secondary">${doc.source}</span>
              ${doc.tokens > 0 ? `<span class="text-muted text-xs">${(doc.tokens / 1000).toFixed(1)}k tokens</span>` : ''}
            </div>
            ${doc.path ? `<div class="mc-doc-path">${doc.path}</div>` : ''}
            ${doc.updated ? `<div class="mc-doc-time">${timeAgo(doc.updated)}</div>` : ''}
          </div>
          <button class="mc-fav-btn ${doc.isFav ? 'active' : ''}" onclick="event.stopPropagation(); window.mcToggleFav('${doc.id}')" title="${doc.isFav ? 'Remove from favorites' : 'Add to favorites'}">
            ${doc.isFav ? '⭐' : '☆'}
          </button>
        </div>
      `).join('')}
    </div>
  `;
}

function getDocType(label) {
  const l = label.toLowerCase();
  if (l.includes('digest') || l.includes('feed')) return 'Digest';
  if (l.includes('news')) return 'News';
  if (l.includes('research')) return 'Research';
  if (l.includes('backup')) return 'Backup';
  if (l.includes('check') || l.includes('availability')) return 'Health Check';
  if (l.includes('review') || l.includes('pr')) return 'Code Review';
  if (l.includes('report')) return 'Report';
  if (l.includes('practice') || l.includes('english')) return 'Learning';
  return 'Document';
}

function getDocIcon(label) {
  const l = label.toLowerCase();
  if (l.includes('digest') || l.includes('feed')) return '📰';
  if (l.includes('news')) return '📡';
  if (l.includes('research')) return '🔬';
  if (l.includes('backup')) return '💾';
  if (l.includes('check') || l.includes('availability')) return '🏥';
  if (l.includes('review') || l.includes('pr')) return '🔍';
  if (l.includes('report')) return '📊';
  if (l.includes('practice') || l.includes('english')) return '📝';
  return '📄';
}

function renderCronTab(agent) {
  // Filter cron jobs by environment (since they are gateway-level, not per-agent)
  const agentCrons = state.cronJobs.filter(j => 
    (j.environment || 'vps').toLowerCase() === agent.environment.toLowerCase()
  );

  if (agentCrons.length === 0) {
    return `<div class="mc-empty-state">
      <div class="mc-empty-icon">⏰</div>
      <div>No cron jobs configured for ${agent.environment.toUpperCase()} gateway</div>
    </div>`;
  }

  return `
    <div class="mc-cron-list">
      ${agentCrons.map((job, idx) => {
        const enabled = job.enabled !== false;
        const lastRun = job.lastRunAt ? timeAgo(new Date(job.lastRunAt)) : 'Never';
        const nextRun = job.nextRunAt ? timeAgo(new Date(job.nextRunAt)) : 'Not scheduled';
        const status = job.lastRunStatus || 'unknown';

        return `
        <div class="mc-cron-card mc-stagger-item" style="animation-delay: ${idx * 40}ms">
          <div class="mc-cron-card-header">
            <div class="mc-cron-card-name">${escapeHtml(job.name || job.id)}</div>
            <label class="mc-toggle">
              <input type="checkbox" ${enabled ? 'checked' : ''} 
                     onchange="window.mcToggleCron('${job.id || job.jobId}', this.checked, '${(job.environment || 'vps').toLowerCase()}')" />
              <span class="mc-toggle-slider"></span>
            </label>
          </div>
          <div class="mc-cron-card-schedule">${formatSchedule(job.schedule)}</div>
          <div class="mc-cron-card-stats">
            <div class="mc-cron-stat">
              <span class="mc-cron-stat-label">Last Run:</span>
              <span class="mc-cron-stat-value">${lastRun}</span>
              <span class="mc-cron-status-indicator ${status}"></span>
            </div>
            <div class="mc-cron-stat">
              <span class="mc-cron-stat-label">Next Run:</span>
              <span class="mc-cron-stat-value">${nextRun}</span>
            </div>
          </div>
          <div class="mc-cron-card-actions">
            <button class="mc-cron-run-btn" data-cron-run="${job.id || job.jobId}"
                    onclick="window.mcRunCron('${job.id || job.jobId}', '${(job.environment || 'vps').toLowerCase()}')">
              ▶ Run Now
            </button>
          </div>
        </div>
        `;
      }).join('')}
    </div>
  `;
}

function renderMetricsTab(agent) {
  // Calculate metrics from raw sessions
  const agentSessions = state.rawSessions.filter(s =>
    s.key.startsWith(`agent:${agent.agentId}:`) &&
    s.environment?.toLowerCase() === agent.environment
  );

  const totalTokens = agentSessions.reduce((sum, s) => sum + (s.totalTokens || 0), 0);
  const sessionCount = agentSessions.length;

  // Model breakdown
  const modelStats = {};
  agentSessions.forEach(s => {
    const model = s.model || 'Unknown';
    if (!modelStats[model]) modelStats[model] = { count: 0, tokens: 0 };
    modelStats[model].count++;
    modelStats[model].tokens += s.totalTokens || 0;
  });

  const estCost = (totalTokens / 1000000) * 15; // ~$15 per 1M tokens

  // Active time calculation (time since earliest session today)
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const todaySessions = agentSessions.filter(s => {
    const updated = new Date(s.updatedAt);
    return updated >= todayStart;
  });
  const earliestToday = todaySessions.length > 0
    ? Math.min(...todaySessions.map(s => new Date(s.createdAt || s.updatedAt).getTime()))
    : null;
  const activeTime = earliestToday
    ? Math.floor((now - earliestToday) / 1000 / 60)
    : 0;

  return `
    <div class="mc-metrics-container">
      <div class="mc-metrics-grid">
        <div class="mc-metric-card">
          <div class="mc-metric-icon">🪙</div>
          <div class="mc-metric-value">${(totalTokens / 1000).toFixed(1)}k</div>
          <div class="mc-metric-label">Total Tokens</div>
        </div>

        <div class="mc-metric-card">
          <div class="mc-metric-icon">💬</div>
          <div class="mc-metric-value">${sessionCount}</div>
          <div class="mc-metric-label">Sessions</div>
        </div>

        <div class="mc-metric-card">
          <div class="mc-metric-icon">💰</div>
          <div class="mc-metric-value">$${estCost.toFixed(2)}</div>
          <div class="mc-metric-label">Est. Cost</div>
        </div>

        <div class="mc-metric-card">
          <div class="mc-metric-icon">⏱️</div>
          <div class="mc-metric-value">${activeTime}m</div>
          <div class="mc-metric-label">Active Today</div>
        </div>
      </div>

      <div class="mc-model-breakdown">
        <h3 class="mc-section-subtitle">Model Usage</h3>
        ${Object.entries(modelStats).map(([model, stats]) => `
          <div class="mc-model-stat-row">
            <div class="mc-model-name">${shortModel(model)}</div>
            <div class="mc-model-stats">
              <span>${stats.count} sessions</span>
              <span>${(stats.tokens / 1000).toFixed(1)}k tokens</span>
            </div>
          </div>
        `).join('')}
      </div>
    </div>
  `;
}

function renderInspectorPanel() {
  const agent = state.selectedAgentId ? state.agents.find(a => a.id === state.selectedAgentId) : null;
  
  return `
    <div class="mc-inspector">
      <div class="mc-inspector-header">Inspector</div>
      ${agent ? renderAgentInspector(agent) : renderGeneralInspector()}
    </div>
  `;
}

function renderGeneralInspector() {
  return `
    <div class="mc-inspector-body">
      <div class="mc-inspector-section">
        <h3 class="mc-inspector-label">System Status</h3>
        <div class="mc-inspector-row">
          <span>Gateway</span>
          <span class="text-success">Online</span>
        </div>
         <div class="mc-inspector-row">
          <span>Polling</span>
          <span class="text-success">10s</span>
        </div>
      </div>
      <div class="text-muted text-sm p-4 text-center">
        Select an agent to inspect details and perform actions.
      </div>
    </div>
  `;
}

function renderAgentInspector(agent) {
  return `
    <div class="mc-inspector-body">
      <div class="mc-inspector-section">
        <h3 class="mc-inspector-label">Quick Actions</h3>
        <div class="mc-action-grid">
          <button class="mc-action-btn" onclick="window.mcQuickAction('message', '${agent.id}')" title="Message">
            <span class="text-xl">💬</span>
            <span class="text-xs">Message</span>
          </button>
          <button class="mc-action-btn" onclick="window.mcQuickAction('ping', '${agent.id}')" title="Heartbeat">
            <span class="text-xl">❤️</span>
            <span class="text-xs">Ping</span>
          </button>
           <button class="mc-action-btn" onclick="window.mcQuickAction('logs', '${agent.id}')" title="Logs">
            <span class="text-xl">📋</span>
            <span class="text-xs">Logs</span>
          </button>
           <button class="mc-action-btn" onclick="window.mcQuickAction('files', '${agent.id}')" title="Files">
            <span class="text-xl">🗂️</span>
            <span class="text-xs">Files</span>
          </button>
        </div>
      </div>

      <div class="mc-inspector-section">
        <h3 class="mc-inspector-label">Commands</h3>
        <div class="mc-action-grid">
          <button class="mc-action-btn" onclick="window.mcOpenCommand('model', '${agent.id}')" title="Change Model">
            <span class="text-xl">🔧</span>
            <span class="text-xs">Model</span>
          </button>
          <button class="mc-action-btn mc-action-danger" onclick="window.mcOpenCommand('restart', '${agent.id}')" title="Restart Gateway">
            <span class="text-xl">🔄</span>
            <span class="text-xs">Restart</span>
          </button>
        </div>
      </div>

      <div class="mc-inspector-section">
        <h3 class="mc-inspector-label">Session Info</h3>
        <div class="mc-inspector-row">
          <span class="text-muted">ID</span>
          <span class="text-xs font-mono">${agent.agentId}</span>
        </div>
        <div class="mc-inspector-row">
          <span class="text-muted">Env</span>
          <span class="text-xs">${agent.environment}</span>
        </div>
        <div class="mc-inspector-row">
          <span class="text-muted">Key</span>
          <span class="text-xs font-mono truncate" style="max-width: 150px" title="${agent.sessionKey}">${agent.sessionKey}</span>
        </div>
         <div class="mc-inspector-row">
          <span class="text-muted">Model</span>
          <span class="text-xs">${agent.model}</span>
        </div>
      </div>
    </div>
  `;
}


function renderCommandBar() {
  return `
    <div class="mc-command-bar">
      <div class="mc-search-container">
        <span class="mc-search-icon">🔍</span>
        <input type="text" id="mc-search-input" class="mc-search-input"
               placeholder="Search agents, cron jobs, sessions... (Press /)"
               value="${state.searchQuery}" />
        <span class="mc-search-hint">/</span>
      </div>
    </div>
  `;
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS & EVENT HANDLERS
// ─────────────────────────────────────────────────────────────────────────────

function attachEventListeners() {
  // Attach global handlers to window for inline onclicks
  window.mcSelectAgent = (agentId) => {
    state.selectedAgentId = agentId;
    state.activeTab = 'conversations';
    fetchAgentHistory(agentId); // Fetch history when selecting
    rerenderMissionControl();
    // On mobile, scroll to the main content area so detail view is visible
    requestAnimationFrame(() => {
      const mainContent = document.querySelector('.mc-main-content');
      if (mainContent && window.innerWidth < 768) {
        mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  };

  window.mcDeselectAgent = () => {
    state.selectedAgentId = null;
    rerenderMissionControl();
  };

  window.mcShowMobileActions = () => {
    const agent = state.agents.find(a => a.id === state.selectedAgentId);
    if (!agent) return;
    const container = document.querySelector('.mission-control-container');
    if (!container) return;
    container.insertAdjacentHTML('beforeend', `
      <div class="mc-overlay-backdrop" onclick="window.mcCloseOverlay()"></div>
      <div class="mc-mobile-actions-sheet">
        <div class="mc-mobile-sheet-header">
          <span>⚡ ${escapeHtml(agent.name)} Actions</span>
          <button class="mc-msg-close" onclick="window.mcCloseOverlay()">✕</button>
        </div>
        <div class="mc-mobile-sheet-body">
          <div class="mc-action-grid" style="grid-template-columns: repeat(4, 1fr);">
            <button class="mc-action-btn" onclick="window.mcCloseOverlay(); window.mcQuickAction('message', '${agent.id}')">
              <span class="text-xl">💬</span><span class="text-xs">Message</span>
            </button>
            <button class="mc-action-btn" onclick="window.mcCloseOverlay(); window.mcQuickAction('ping', '${agent.id}')">
              <span class="text-xl">❤️</span><span class="text-xs">Ping</span>
            </button>
            <button class="mc-action-btn" onclick="window.mcCloseOverlay(); window.mcQuickAction('logs', '${agent.id}')">
              <span class="text-xl">📋</span><span class="text-xs">Logs</span>
            </button>
            <button class="mc-action-btn" onclick="window.mcCloseOverlay(); window.mcQuickAction('files', '${agent.id}')">
              <span class="text-xl">🗂️</span><span class="text-xs">Files</span>
            </button>
          </div>
          <div class="mc-action-grid" style="grid-template-columns: repeat(2, 1fr); margin-top: var(--space-3);">
            <button class="mc-action-btn" onclick="window.mcCloseOverlay(); window.mcOpenCommand('model', '${agent.id}')">
              <span class="text-xl">🔧</span><span class="text-xs">Change Model</span>
            </button>
            <button class="mc-action-btn mc-action-danger" onclick="window.mcCloseOverlay(); window.mcOpenCommand('restart', '${agent.id}')">
              <span class="text-xl">🔄</span><span class="text-xs">Restart</span>
            </button>
          </div>
        </div>
      </div>
    `);
    attachOverlayHandlers();
  };

  window.mcSwitchTab = (tab) => {
    state.activeTab = tab;
    // Targeted update: only swap tab content + tab buttons, preserve scroll
    const tabContent = document.querySelector('.mc-tab-content');
    const tabs = document.querySelector('.mc-tabs');
    const agent = state.agents.find(a => a.id === state.selectedAgentId);
    if (tabContent && agent) {
      tabContent.innerHTML = 
        tab === 'conversations' ? renderConversationsTab(agent) :
        tab === 'documents' ? renderDocumentsTab(agent) :
        tab === 'cron' ? renderCronTab(agent) :
        tab === 'metrics' ? renderMetricsTab(agent) : '';
      // Update active tab styling
      if (tabs) {
        tabs.querySelectorAll('.mc-tab').forEach(t => {
          t.classList.toggle('active', t.textContent.toLowerCase().replace(/\s/g, '') === tab.toLowerCase().replace(/\s/g, ''));
        });
      }
      attachDocTouchHandlers();
    } else {
      rerenderMissionControl();
    }
  };

  window.mcToggleFav = (docId) => {
    toggleFavorite(docId);
    // Only re-render the documents list, not the whole page
    const tabContent = document.querySelector('.mc-tab-content');
    const agent = state.agents.find(a => a.id === state.selectedAgentId);
    if (tabContent && agent && state.activeTab === 'documents') {
      tabContent.innerHTML = renderDocumentsTab(agent);
      attachDocTouchHandlers();
    } else {
      rerenderMissionControl();
    }
  };

  window.mcOpenDoc = async (docId, path, sessionKey) => {
    state.docViewer = { docId, title: 'Loading...', content: '', loading: true, metadata: {} };
    // Inject overlay without full re-render
    const container = document.querySelector('.mission-control-container');
    if (container) {
      container.insertAdjacentHTML('beforeend', renderDocViewer());
      attachOverlayHandlers();
    }

    try {
      let content = '';
      let title = '';
      
      if (path && !sessionKey) {
        // Pinned workspace file — fetch content from file server
        title = path.split('/').pop();
        const agent = state.selectedAgent;
        const env = agent ? agent.environment : 'vps';
        const fileData = await readAgentFile(path, env);
        if (fileData.ok && fileData.content) {
          content = fileData.content;
        } else {
          content = `# 📌 ${title}\n\n**Location:** \`${path}\`\n\n---\n\n⚠️ Could not fetch file content: ${fileData.error || 'Unknown error'}\n\nThe file server may not be running on the ${env.toUpperCase()} host.`;
        }
      } else if (sessionKey) {
        // Session-generated doc — fetch from session history
        const data = await fetchHistoryApi(sessionKey);
        const messages = data.messages || data.history || [];
        // Find last assistant message
        const assistantMsgs = messages.filter(m => m.role === 'assistant');
        if (assistantMsgs.length > 0) {
          const lastContent = assistantMsgs[assistantMsgs.length - 1].content || '';
          content = typeof lastContent === 'string' ? lastContent : 
            Array.isArray(lastContent) ? lastContent.map(c => c.text || '').join('\n') : 
            JSON.stringify(lastContent);
        } else if (messages.length > 0) {
          // Show all messages if no assistant messages
          content = messages.map(m => {
            const c = m.content || '';
            const text = typeof c === 'string' ? c : Array.isArray(c) ? c.map(x => x.text || '').join('\n') : JSON.stringify(c);
            return `**${m.role}:** ${text}`;
          }).join('\n\n---\n\n');
        } else {
          content = 'No content found in session history';
        }
        // Extract readable title from session key
        const label = (state.rawSessions || []).find(s => s.key === sessionKey)?.label;
        title = label || sessionKey.split(':').slice(-1)[0] || sessionKey;
      }

      state.docViewer.content = content;
      state.docViewer.title = title;
      state.docViewer.loading = false;
      
      // Update overlay content
      const viewer = document.querySelector('.mc-doc-viewer');
      if (viewer) {
        const body = viewer.querySelector('.mc-doc-viewer-body');
        if (body) {
          body.innerHTML = content 
            ? `<div class="mc-doc-rendered">${renderMarkdown(content)}</div>`
            : '<div class="mc-empty-state">No content available</div>';
        }
        const titleEl = viewer.querySelector('.mc-doc-viewer-title');
        if (titleEl) titleEl.textContent = `📄 ${title}`;
      }
    } catch (err) {
      state.docViewer.loading = false;
      state.docViewer.content = `Error: ${err.message}`;
      const body = document.querySelector('.mc-doc-viewer-body');
      if (body) body.innerHTML = `<div class="mc-empty-state">Error loading document: ${err.message}</div>`;
    }
  };

  window.mcRefreshHistory = (agentId) => {
    fetchAgentHistory(agentId).then(() => rerenderMissionControl());
  };

  window.mcQuickAction = (action, agentId) => {
    const agent = state.agents.find(a => a.id === agentId);
    if (!agent) return;

    switch (action) {
      case 'message':
        state.messageModal = { 
          agentId: agent.id, 
          sessionKey: agent.sessionKey, 
          environment: agent.environment, 
          agentName: agent.name,
          sending: false 
        };
        // Inject modal without full re-render
        const container = document.querySelector('.mission-control-container');
        if (container) {
          container.insertAdjacentHTML('beforeend', renderMessageModal());
          attachOverlayHandlers();
          const input = document.getElementById('mc-msg-input');
          if (input) input.focus();
        }
        break;
      case 'ping':
        handlePing(agent);
        break;
      case 'logs':
        // Open conversations tab for the agent
        state.activeTab = 'conversations';
        rerenderMissionControl();
        break;
      case 'files':
        // Open documents tab for the agent
        state.activeTab = 'documents';
        rerenderMissionControl();
        break;
    }
  };

  window.mcSendMessage = async () => {
    const input = document.getElementById('mc-msg-input');
    if (!input || !state.messageModal) return;
    const message = input.value.trim();
    if (!message) return;

    state.messageModal.sending = true;
    const sendBtn = document.querySelector('.mc-msg-send-btn');
    if (sendBtn) {
      sendBtn.textContent = 'Sending...';
      sendBtn.disabled = true;
    }

    try {
      const result = await sendAgentMessage(
        state.messageModal.sessionKey,
        message,
        state.messageModal.environment
      );

      if (result.ok) {
        // Show success feedback
        if (sendBtn) sendBtn.textContent = '✅ Sent!';
        setTimeout(() => window.mcCloseOverlay(), 1000);
      } else {
        if (sendBtn) {
          sendBtn.textContent = '❌ Failed';
          sendBtn.disabled = false;
        }
      }
    } catch (err) {
      if (sendBtn) {
        sendBtn.textContent = '❌ Error';
        sendBtn.disabled = false;
      }
    }
  };

  window.mcCloseOverlay = () => {
    state.messageModal = null;
    state.docViewer = null;
    state.replaySession = null;
    state.commandModal = null;
    document.querySelectorAll('.mc-overlay-backdrop').forEach(el => el.remove());
    document.querySelectorAll('.mc-msg-modal').forEach(el => el.remove());
    document.querySelectorAll('.mc-doc-viewer').forEach(el => el.remove());
    document.querySelectorAll('.mc-replay-panel').forEach(el => el.remove());
    document.querySelectorAll('.mc-cmd-modal').forEach(el => el.remove());
  };

  window.mcSetActivityFilter = (filter) => {
    state.activityFilter = filter;
    const stream = document.querySelector('.mc-activity-stream');
    const header = document.querySelector('.mc-activity-header');
    if (stream) {
      stream.innerHTML = renderActivityStream();
    }
    if (header) {
      header.querySelectorAll('.mc-filter-btn').forEach(btn => {
        btn.classList.toggle('active', btn.textContent.toLowerCase() === filter);
      });
    }
  };

  // Feature 4: Conversation Replay (uses transcript files for full history)
  window.mcOpenReplay = async (sessionKey, environment, transcriptPath, agentId) => {
    state.replaySession = { sessionKey, environment, transcriptPath, agentId, messages: [], loading: true, total: 0, hasMore: false, loadingMore: false, offset: 0 };
    const container = document.querySelector('.mission-control-container');
    if (container) {
      container.insertAdjacentHTML('beforeend', renderConversationReplay());
      attachOverlayHandlers();
    }

    try {
      let data;
      if (transcriptPath && agentId) {
        // Use transcript file for full history
        data = await fetchTranscript(agentId, transcriptPath, environment, 500, 0);
      } else {
        // Fallback to session history
        data = await fetchConversationReplay(sessionKey, environment, 500, 0);
      }
      state.replaySession.messages = data.messages || [];
      state.replaySession.total = data.total || state.replaySession.messages.length;
      state.replaySession.hasMore = data.hasMore || false;
      state.replaySession.offset = data.offset || state.replaySession.messages.length;
      state.replaySession.loading = false;

      const body = document.querySelector('.mc-replay-body');
      if (body) {
        body.innerHTML = (state.replaySession.hasMore ? `<button class="mc-load-more-btn" onclick="window.mcLoadMoreReplay()">⬆ Load older messages (${state.replaySession.total - state.replaySession.offset} remaining)</button>` : '') + renderReplayMessages(state.replaySession.messages);
        body.scrollTop = body.scrollHeight;
      }
    } catch (err) {
      state.replaySession.loading = false;
      const body = document.querySelector('.mc-replay-body');
      if (body) body.innerHTML = `<div class="mc-empty-state">Error: ${err.message}</div>`;
    }
  };

  window.mcLoadMoreReplay = async () => {
    if (!state.replaySession || state.replaySession.loadingMore || !state.replaySession.hasMore) return;
    state.replaySession.loadingMore = true;
    
    const btn = document.querySelector('.mc-load-more-btn');
    if (btn) btn.textContent = '⏳ Loading...';

    try {
      let data;
      if (state.replaySession.transcriptPath && state.replaySession.agentId) {
        data = await fetchTranscript(
          state.replaySession.agentId,
          state.replaySession.transcriptPath,
          state.replaySession.environment,
          500,
          state.replaySession.offset
        );
      } else {
        data = await fetchConversationReplay(
          state.replaySession.sessionKey, 
          state.replaySession.environment, 
          500, 
          state.replaySession.offset
        );
      }
      const olderMessages = data.messages || [];
      state.replaySession.messages = [...olderMessages, ...state.replaySession.messages];
      state.replaySession.offset = data.offset || (state.replaySession.offset + olderMessages.length);
      state.replaySession.hasMore = data.hasMore || false;
      state.replaySession.loadingMore = false;

      const body = document.querySelector('.mc-replay-body');
      if (body) {
        const scrollBottom = body.scrollHeight - body.scrollTop;
        body.innerHTML = (state.replaySession.hasMore ? `<button class="mc-load-more-btn" onclick="window.mcLoadMoreReplay()">⬆ Load older messages (${state.replaySession.total - state.replaySession.offset} remaining)</button>` : `<div class="mc-replay-top-marker">— Beginning of conversation —</div>`) + renderReplayMessages(state.replaySession.messages);
        // Maintain scroll position
        body.scrollTop = body.scrollHeight - scrollBottom;
      }
    } catch (err) {
      state.replaySession.loadingMore = false;
      if (btn) btn.textContent = '⬆ Error loading — tap to retry';
    }
  };

  // Feature 6: Agent Commands
  window.mcOpenCommand = (type, agentId) => {
    const agent = state.agents.find(a => a.id === agentId);
    if (!agent) return;
    state.commandModal = { type, agent, loading: false };
    const container = document.querySelector('.mission-control-container');
    if (container) {
      container.insertAdjacentHTML('beforeend', renderCommandModal());
      attachOverlayHandlers();
    }
  };

  window.mcExecuteCommand = async (command, params) => {
    if (!state.commandModal) return;
    const { agent } = state.commandModal;
    state.commandModal.loading = true;

    const statusEl = document.querySelector('.mc-cmd-status');
    if (statusEl) statusEl.textContent = 'Executing...';

    try {
      const result = await sendAgentCommand(command, agent.environment, params);
      if (result.ok) {
        if (statusEl) statusEl.innerHTML = '<span class="mc-ping-ok">✅ Success</span>';
        setTimeout(() => window.mcCloseOverlay(), 1500);
      } else {
        if (statusEl) statusEl.innerHTML = `<span class="mc-ping-fail">❌ ${result.error || 'Failed'}</span>`;
      }
    } catch (err) {
      if (statusEl) statusEl.innerHTML = `<span class="mc-ping-fail">❌ ${err.message}</span>`;
    }
    state.commandModal.loading = false;
  };

  window.mcToggleCron = async (jobId, enabled, environment) => {
    try {
      const result = await sendAgentCommand('toggle_cron', environment, { jobId, enabled });
      if (result.ok) {
        // Update local state
        const job = state.cronJobs.find(j => (j.id || j.jobId) === jobId);
        if (job) job.enabled = enabled;
        // Re-render cron tab
        const tabContent = document.querySelector('.mc-tab-content');
        const agent = state.agents.find(a => a.id === state.selectedAgentId);
        if (tabContent && agent && state.activeTab === 'cron') {
          tabContent.innerHTML = renderCronTab(agent);
        }
      }
    } catch (err) {
      console.error('Toggle cron failed:', err);
    }
  };

  window.mcRunCron = async (jobId, environment) => {
    try {
      const result = await sendAgentCommand('run_cron', environment, { jobId });
      if (result.ok) {
        // Brief flash on the button
        const btn = document.querySelector(`[data-cron-run="${jobId}"]`);
        if (btn) {
          btn.textContent = '✅ Triggered';
          setTimeout(() => { btn.textContent = '▶ Run Now'; }, 2000);
        }
      }
    } catch (err) {
      console.error('Run cron failed:', err);
    }
  };

  // Search input handler
  const searchInput = document.getElementById('mc-search-input');
  if (searchInput) {
    searchInput.addEventListener('input', (e) => {
      state.searchQuery = e.target.value;
      rerenderMissionControl();
    });
  }

  // Event delegation for agent cards (mobile touch fix)
  // Track touch start position to distinguish tap from scroll
  let touchStartX = 0, touchStartY = 0;
  document.querySelectorAll('.mc-agent-card[data-agent-id]').forEach(card => {
    card.addEventListener('touchstart', (e) => {
      const touch = e.touches[0];
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
    }, { passive: true });
    
    card.addEventListener('touchend', (e) => {
      const touch = e.changedTouches[0];
      const dx = Math.abs(touch.clientX - touchStartX);
      const dy = Math.abs(touch.clientY - touchStartY);
      // Only trigger selection if finger moved less than 10px (tap, not scroll)
      if (dx < 10 && dy < 10) {
        e.preventDefault();
        const agentId = card.dataset.agentId;
        if (agentId) window.mcSelectAgent(agentId);
      }
    }, { passive: false });
  });

  // Keyboard shortcuts
  document.addEventListener('keydown', handleKeyboardShortcuts);
}

function attachDocTouchHandlers() {
  // Add touch handlers for fav buttons on mobile (prevent scroll-triggering favorites)
  document.querySelectorAll('.mc-fav-btn').forEach(btn => {
    let tx = 0, ty = 0;
    btn.addEventListener('touchstart', e => {
      tx = e.touches[0].clientX;
      ty = e.touches[0].clientY;
    }, { passive: true });
    btn.addEventListener('touchend', e => {
      const dx = Math.abs(e.changedTouches[0].clientX - tx);
      const dy = Math.abs(e.changedTouches[0].clientY - ty);
      if (dx < 10 && dy < 10) {
        e.preventDefault();
        e.stopPropagation();
        btn.click();
      }
    }, { passive: false });
  });
}

// ─── Feature 1: Quick Actions ──────────────────────

async function handlePing(agent) {
  // Update ping button to show loading
  const pingBtn = document.querySelector(`.mc-action-btn[title="Heartbeat"]`);
  if (pingBtn) {
    pingBtn.innerHTML = '<div class="mc-ping-spinner"></div>';
    pingBtn.disabled = true;
  }

  const result = await pingAgent(agent.environment);
  state.pingResults[agent.id] = {
    status: result.ok ? 'online' : 'offline',
    latency: result.latency,
    timestamp: Date.now(),
  };

  // Show inline result
  if (pingBtn) {
    if (result.ok) {
      pingBtn.innerHTML = `<span class="mc-ping-ok">✅ ${result.latency}ms</span>`;
    } else {
      pingBtn.innerHTML = `<span class="mc-ping-fail">❌ Offline</span>`;
    }
    pingBtn.disabled = false;
    // Reset after 5 seconds
    setTimeout(() => {
      if (pingBtn) pingBtn.innerHTML = '❤️';
    }, 8000);
  }
}

function renderMessageModal() {
  if (!state.messageModal) return '';
  const { agentName, environment } = state.messageModal;
  
  return `
    <div class="mc-overlay-backdrop" onclick="window.mcCloseOverlay()"></div>
    <div class="mc-msg-modal">
      <div class="mc-msg-modal-header">
        <h3>💬 Message ${escapeHtml(agentName)}</h3>
        <span class="mc-badge small">${environment.toUpperCase()}</span>
        <button class="mc-msg-close" onclick="window.mcCloseOverlay()">✕</button>
      </div>
      <div class="mc-msg-modal-body">
        <textarea id="mc-msg-input" class="mc-msg-textarea" 
                  placeholder="Type a message to ${escapeHtml(agentName)}..."
                  rows="4"></textarea>
        <div class="mc-msg-hint">Message will be sent to the agent's persistent session</div>
      </div>
      <div class="mc-msg-modal-footer">
        <button class="mc-msg-cancel-btn" onclick="window.mcCloseOverlay()">Cancel</button>
        <button class="mc-msg-send-btn" onclick="window.mcSendMessage()">Send Message</button>
      </div>
    </div>
  `;
}

// ─── Feature 2: Document Viewer ──────────────────────

function renderDocViewer() {
  if (!state.docViewer) return '';
  const { title, content, loading } = state.docViewer;

  return `
    <div class="mc-overlay-backdrop" onclick="window.mcCloseOverlay()"></div>
    <div class="mc-doc-viewer">
      <div class="mc-doc-viewer-header">
        <h3 class="mc-doc-viewer-title">📄 ${title || 'Document'}</h3>
        <button class="mc-msg-close" onclick="window.mcCloseOverlay()">✕</button>
      </div>
      <div class="mc-doc-viewer-body custom-scrollbar">
        ${loading 
          ? '<div class="mc-doc-loading"><div class="mc-ping-spinner"></div> Loading document...</div>'
          : content 
            ? `<div class="mc-doc-rendered">${renderMarkdown(content)}</div>`
            : '<div class="mc-empty-state">No content available</div>'
        }
      </div>
    </div>
  `;
}

// ─── Feature 4: Conversation Replay ──────────────────────

function renderConversationReplay() {
  if (!state.replaySession) return '';
  const { sessionKey, loading } = state.replaySession;
  const label = (state.rawSessions || []).find(s => s.key === sessionKey)?.label || sessionKey;

  return `
    <div class="mc-overlay-backdrop" onclick="window.mcCloseOverlay()"></div>
    <div class="mc-replay-panel">
      <div class="mc-replay-header">
        <h3>💬 ${label}</h3>
        <span class="text-muted text-xs">${sessionKey}</span>
        <button class="mc-msg-close" onclick="window.mcCloseOverlay()">✕</button>
      </div>
      <div class="mc-replay-body custom-scrollbar">
        ${loading 
          ? '<div class="mc-doc-loading"><div class="mc-ping-spinner"></div> Loading conversation...</div>'
          : renderReplayMessages(state.replaySession.messages)
        }
      </div>
    </div>
  `;
}

function renderReplayMessages(messages) {
  if (!messages || messages.length === 0) {
    return '<div class="mc-empty-state">No messages in this session</div>';
  }

  // Group messages into conversation turns
  // A turn ends when: a new user message appears, or an assistant produces final text output
  const turns = [];
  let currentTurn = { messages: [] };
  let hasPendingToolActivity = false;
  
  for (const msg of messages) {
    if (msg.role === 'user' && currentTurn.messages.length > 0) {
      turns.push(currentTurn);
      currentTurn = { messages: [] };
      hasPendingToolActivity = false;
    }
    
    currentTurn.messages.push(msg);
    
    // Track tool activity
    if (msg.role === 'tool' || msg.role === 'toolResult' || msg.role === 'function') {
      hasPendingToolActivity = true;
    }
    
    // Split after assistant message with real text content (not just tool calls)
    if (msg.role === 'assistant') {
      let hasText = false;
      const content = msg.content || '';
      if (typeof content === 'string' && content.trim().length > 0) {
        hasText = true;
      } else if (Array.isArray(content)) {
        hasText = content.some(c => c.type === 'text' && c.text && c.text.trim().length > 0);
      }
      // Only split if there's no tool_calls (meaning this is a final response, not mid-chain)
      const hasToolCalls = (msg.tool_calls && msg.tool_calls.length > 0) || 
        (Array.isArray(content) && content.some(c => c.type === 'toolCall'));
      
      if (hasText && !hasToolCalls) {
        turns.push(currentTurn);
        currentTurn = { messages: [] };
        hasPendingToolActivity = false;
      }
    }
  }
  if (currentTurn.messages.length > 0) turns.push(currentTurn);

  return turns.map(turn => renderTurn(turn.messages)).join('');
}

function renderTurn(messages) {
  const parts = [];
  const toolActivity = [];
  let finalAssistantContent = '';
  let finalAssistantTimestamp = '';
  let thinkingHtml = '';
  let userMsg = null;

  for (const msg of messages) {
    const timestamp = msg.timestamp 
      ? new Date(msg.timestamp).toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', day: '2-digit', month: 'short' })
      : '';

    if (msg.role === 'user') {
      let content = msg.content || '';
      if (Array.isArray(content)) content = content.map(c => c.text || '').join('\n');
      else if (typeof content !== 'string') content = JSON.stringify(content);
      
      // Extract human message from system events
      const { humanText, systemEvents } = extractHumanMessage(content);
      userMsg = { content: humanText, systemEvents, timestamp };
      continue;
    }

    if (msg.role === 'system') {
      parts.push(`<div class="mc-replay-system">${escapeHtml(String(msg.content || '[system]'))}${timestamp ? ` <span class="mc-replay-time">${timestamp}</span>` : ''}</div>`);
      continue;
    }

    // Tool results
    if (msg.role === 'tool' || msg.role === 'function' || msg.role === 'toolResult') {
      const toolName = msg.name || msg.tool || msg.toolName || 'tool';
      let contentStr = '';
      if (typeof msg.content === 'string') contentStr = msg.content;
      else if (Array.isArray(msg.content)) contentStr = msg.content.map(c => c.text || JSON.stringify(c)).join('\n');
      else contentStr = JSON.stringify(msg.content || {});
      toolActivity.push({ type: 'result', name: toolName, content: contentStr, timestamp });
      continue;
    }

    // Assistant messages
    if (msg.role === 'assistant') {
      let content = msg.content || '';
      
      // Handle array content (thinking + text + toolCall)
      if (Array.isArray(content)) {
        const thinkingBlocks = content.filter(c => c.type === 'thinking' && c.thinking);
        const textBlocks = content.filter(c => (c.type === 'text' && c.text) || (c.text && !c.type));
        const toolCallBlocks = content.filter(c => c.type === 'toolCall');
        
        if (thinkingBlocks.length > 0) {
          const thinkingText = thinkingBlocks.map(c => c.thinking).join('\n\n');
          const preview = thinkingText.substring(0, 80).replace(/\n/g, ' ');
          thinkingHtml = `
            <details class="mc-replay-thinking">
              <summary>🧠 Reasoning <span class="mc-thinking-preview">${escapeHtml(preview)}${thinkingText.length > 80 ? '...' : ''}</span></summary>
              <div class="mc-thinking-content">${escapeHtml(thinkingText)}</div>
            </details>
          `;
        }
        
        for (const tc of toolCallBlocks) {
          toolActivity.push({ type: 'call', name: tc.name || 'tool', args: tc.arguments, timestamp });
        }
        
        content = textBlocks.map(c => c.text || '').join('\n');
      } else if (typeof content !== 'string') {
        content = JSON.stringify(content);
      }

      // Handle tool_calls array
      if (msg.tool_calls && msg.tool_calls.length > 0) {
        for (const tc of msg.tool_calls) {
          const name = tc.function?.name || tc.name || 'tool';
          toolActivity.push({ type: 'call', name, args: tc.function?.arguments || tc.arguments, timestamp });
        }
      }

      // If this message has text content, it's a potential final output
      if (content.trim()) {
        finalAssistantContent = content;
        finalAssistantTimestamp = timestamp;
      }
    }
  }

  // Render user message
  if (userMsg) {
    const escaped = userMsg.content.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const systemHtml = userMsg.systemEvents && userMsg.systemEvents.length > 0
      ? `<details class="mc-replay-system-events"><summary>${userMsg.systemEvents.length} system event${userMsg.systemEvents.length > 1 ? 's' : ''}</summary><pre class="mc-system-events-content">${escapeHtml(userMsg.systemEvents.join('\n'))}</pre></details>`
      : '';
    parts.push(`
      <div class="mc-replay-msg mc-replay-user">
        <div class="mc-replay-role">👤 User${userMsg.timestamp ? ` <span class="mc-replay-time">${userMsg.timestamp}</span>` : ''}</div>
        ${systemHtml}
        <div class="mc-replay-content">${escaped || '<em class="text-muted">(system trigger)</em>'}</div>
      </div>
    `);
  }

  // Render tool activity as a single collapsed block
  if (toolActivity.length > 0) {
    const toolNames = [...new Set(toolActivity.filter(t => t.type === 'call').map(t => t.name))];
    const callCount = toolActivity.filter(t => t.type === 'call').length;
    const summary = callCount > 0 
      ? `Used ${callCount} tool${callCount > 1 ? 's' : ''}: ${toolNames.slice(0, 4).join(', ')}${toolNames.length > 4 ? '...' : ''}`
      : `${toolActivity.length} tool result${toolActivity.length > 1 ? 's' : ''}`;
    
    const activityHtml = toolActivity.map(t => {
      if (t.type === 'call') {
        const argsStr = t.args ? (typeof t.args === 'string' ? t.args : JSON.stringify(t.args)) : '';
        const truncArgs = argsStr.length > 200 ? argsStr.substring(0, 200) + '...' : argsStr;
        return `<div class="mc-activity-item mc-activity-call">⚡ <strong>${escapeHtml(t.name)}</strong>${truncArgs ? `<pre>${escapeHtml(truncArgs)}</pre>` : ''}</div>`;
      } else {
        const truncContent = t.content.length > 300 ? t.content.substring(0, 300) + '...' : t.content;
        return `<div class="mc-activity-item mc-activity-result">🔧 <strong>${escapeHtml(t.name)}</strong><pre>${escapeHtml(truncContent)}</pre></div>`;
      }
    }).join('');

    parts.push(`
      <details class="mc-replay-activity">
        <summary>🔄 ${summary}</summary>
        <div class="mc-activity-items">${activityHtml}</div>
      </details>
    `);
  }

  // Render final assistant output
  if (finalAssistantContent.trim()) {
    // Check for inline reasoning
    if (!thinkingHtml) {
      const inlineResult = splitInlineReasoning(finalAssistantContent);
      if (inlineResult) {
        const preview = inlineResult.reasoning.substring(0, 80).replace(/\n/g, ' ');
        thinkingHtml = `
          <details class="mc-replay-thinking">
            <summary>🧠 Reasoning <span class="mc-thinking-preview">${escapeHtml(preview)}${inlineResult.reasoning.length > 80 ? '...' : ''}</span></summary>
            <div class="mc-thinking-content">${escapeHtml(inlineResult.reasoning)}</div>
          </details>
        `;
        finalAssistantContent = inlineResult.output;
      }
    }

    const rendered = renderMarkdown(finalAssistantContent);
    parts.push(`
      <div class="mc-replay-msg mc-replay-assistant">
        <div class="mc-replay-role">🤖 Assistant${finalAssistantTimestamp ? ` <span class="mc-replay-time">${finalAssistantTimestamp}</span>` : ''}</div>
        ${thinkingHtml}
        <div class="mc-replay-content">${rendered}</div>
      </div>
    `);
  } else if (thinkingHtml) {
    // Only thinking, no output text
    parts.push(`
      <div class="mc-replay-msg mc-replay-assistant">
        <div class="mc-replay-role">🤖 Assistant${finalAssistantTimestamp ? ` <span class="mc-replay-time">${finalAssistantTimestamp}</span>` : ''}</div>
        ${thinkingHtml}
      </div>
    `);
  }

  return parts.join('');
}

// Extract human message from mixed system events + user text
function extractHumanMessage(content) {
  const lines = content.split('\n');
  const systemEvents = [];
  const humanLines = [];
  
  for (const line of lines) {
    // System event patterns
    if (/^\[?\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(line.trim()) ||
        /^System: \[/.test(line.trim()) ||
        /^\[media attached:/.test(line.trim()) ||
        /^To send an image back/.test(line.trim()) ||
        /^MEDIA:/.test(line.trim()) ||
        /^Exec (completed|failed)/.test(line.trim()) ||
        /^Command still running/.test(line.trim()) ||
        /^\[message_id:/.test(line.trim())) {
      systemEvents.push(line);
    } else if (/^\[Telegram .+\d{4}-\d{2}-\d{2}/.test(line.trim())) {
      // Extract the actual message after the Telegram header
      const msgMatch = line.match(/\] (.+)$/);
      if (msgMatch) humanLines.push(msgMatch[1]);
      systemEvents.push(line.split(']')[0] + ']');
    } else if (line.trim()) {
      humanLines.push(line);
    }
  }
  
  return {
    humanText: humanLines.join('\n').trim(),
    systemEvents: systemEvents.length > 0 ? systemEvents : null,
  };
}

// Detect and split inline reasoning from actual output in assistant messages
function splitInlineReasoning(text) {

  // Common patterns where agent "thinks out loud" before producing output
  const reasoningStarters = /^(Now |Let me |I('ll| need| should| have| can see| notice| want| see)|OK[,.]|Alright|First|Looking|Checking|Based on|The |This |Here|So |Wait|Hmm|Actually|Let's|Okay|I'm going|Time to|Perfect|Got it|Right)/i;
  
  if (!reasoningStarters.test(text.trim())) return null;
  
  // Look for clear content break patterns
  const breakPatterns = [
    /\n\n(#{1,3} )/,           // Markdown heading
    /\n\n([☀️🌙📡🤖🔒💰🌍⚡🎯📊✅❌🚀💡📋🔧🛡️📱])/,  // Emoji-led section
    /\n\n(---+)\n/,             // Horizontal rule
    /\n\n(\*\*[A-Z])/,          // Bold heading
    /\n\n(━+)/,                 // Unicode line
    /\n\n(Here'?s? (?:the|my|your|a))/i,  // "Here's the..."
    /\n\n(Dear |Hi |Hello )/i,  // Letter/email start
  ];
  
  for (const pattern of breakPatterns) {
    const match = text.match(pattern);
    if (match && match.index > 20 && match.index < text.length * 0.5) {
      const reasoning = text.substring(0, match.index).trim();
      const output = text.substring(match.index).trim();
      // Only split if reasoning is short relative to output
      if (reasoning.length < output.length && reasoning.length > 10) {
        return { reasoning, output };
      }
    }
  }
  
  return null;
}

// ─── Feature 6: Agent Commands ──────────────────────

function renderCommandModal() {
  if (!state.commandModal) return '';
  const { type, agent } = state.commandModal;
  
  let body = '';
  switch (type) {
    case 'restart':
      body = `
        <p>This will restart the <strong>${agent.environment.toUpperCase()}</strong> gateway.</p>
        <p class="text-muted text-sm">All active sessions will be interrupted temporarily.</p>
        <div class="mc-cmd-status"></div>
        <div class="mc-msg-modal-footer">
          <button class="mc-msg-cancel-btn" onclick="window.mcCloseOverlay()">Cancel</button>
          <button class="mc-cmd-danger-btn" onclick="window.mcExecuteCommand('restart')">⚠️ Restart Gateway</button>
        </div>
      `;
      break;
    case 'model':
      body = `
        <p>Change model for <strong>${escapeHtml(agent.name)}</strong>:</p>
        <select id="mc-model-select" class="mc-cmd-select">
          <option value="anthropic-proxy/claude-opus-4-6-thinking">Claude Opus 4.6</option>
          <option value="anthropic-proxy/claude-sonnet-4-5-thinking">Claude Sonnet 4.5</option>
          <option value="google-gemini-cli/gemini-3-pro-preview">Gemini 3 Pro</option>
          <option value="google-gemini-cli/gemini-3-flash-preview">Gemini 3 Flash</option>
        </select>
        <div class="mc-cmd-status"></div>
        <div class="mc-msg-modal-footer">
          <button class="mc-msg-cancel-btn" onclick="window.mcCloseOverlay()">Cancel</button>
          <button class="mc-msg-send-btn" onclick="window.mcExecuteCommand('change_model', { sessionKey: '${agent.sessionKey}', model: document.getElementById('mc-model-select').value })">Apply Model</button>
        </div>
      `;
      break;
    default:
      body = '<p>Unknown command type</p>';
  }

  return `
    <div class="mc-overlay-backdrop" onclick="window.mcCloseOverlay()"></div>
    <div class="mc-cmd-modal">
      <div class="mc-msg-modal-header">
        <h3>${type === 'restart' ? '⚠️ Restart Gateway' : '🔧 Change Model'}</h3>
        <span class="mc-badge small">${agent.environment.toUpperCase()}</span>
        <button class="mc-msg-close" onclick="window.mcCloseOverlay()">✕</button>
      </div>
      <div class="mc-msg-modal-body">
        ${body}
      </div>
    </div>
  `;
}

function attachOverlayHandlers() {
  // Handle Escape key to close overlays
  const handler = (e) => {
    if (e.key === 'Escape') {
      window.mcCloseOverlay();
      document.removeEventListener('keydown', handler);
    }
  };
  document.addEventListener('keydown', handler);

  // Handle Enter key in message modal
  const input = document.getElementById('mc-msg-input');
  if (input) {
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        window.mcSendMessage();
      }
    });
  }
}


function handleKeyboardShortcuts(e) {
  // Only handle if Mission Control is active
  if (!document.querySelector('.mission-control-container')) return;

  // Ignore if typing in an input
  if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
    // Allow Escape to blur/deselect
    if (e.key === 'Escape') {
      e.target.blur();
      window.mcDeselectAgent();
    }
    return;
  }

  switch (e.key) {
    case '/':
      e.preventDefault();
      document.getElementById('mc-search-input')?.focus();
      break;
    case 'Escape':
      e.preventDefault();
      state.searchQuery = '';
      window.mcDeselectAgent();
      break;
    case '1':
    case '2':
    case '3':
    case '4':
    case '5':
    case '6':
    case '7':
    case '8':
      e.preventDefault();
      const index = parseInt(e.key) - 1;
      if (state.agents[index]) {
        window.mcSelectAgent(state.agents[index].id);
      }
      break;
  }
}

function timeAgo(date) {
  const seconds = Math.floor((new Date() - date) / 1000);
  let interval = seconds / 31536000;
  if (interval > 1) return Math.floor(interval) + "y ago";
  interval = seconds / 2592000;
  if (interval > 1) return Math.floor(interval) + "mo ago";
  interval = seconds / 86400;
  if (interval > 1) return Math.floor(interval) + "d ago";
  interval = seconds / 3600;
  if (interval > 1) return Math.floor(interval) + "h ago";
  interval = seconds / 60;
  if (interval > 1) return Math.floor(interval) + "m ago";
  return "just now";
}

function escapeHtml(text) {
  if (!text) return '';
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

